/**
 * Cloudflare Turnstile — loaded on demand, at step 2, not on page load. `05-API-CONTRACT.md`
 * §5.4 requires it only on `/checkout/intent`; rendering the widget the moment the checkout
 * container appears would be exactly the "challenge on a read" `Rest\FundsController`'s own
 * docblock argues against, one layer up: a challenge nobody has decided to donate yet solves.
 *
 * No Subresource Integrity here for the same documented reason `gateway-loader.js` states for
 * Stripe and PayPal: Cloudflare serves this script from an unversioned URL it updates on its own
 * schedule, and a pinned hash would break every checkout the next time it does.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let scriptPromise = null;

/*
 * After rc5 (staging item 2, a real iPhone: "We couldn't finish a quick security check" with no request reaching the site).
 * Cloudflare: "A defined error callback doesn't disable automatic retry. … Turnstile will automatically retry upon encountering a
 * problem" (https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/). The error callback below used to end
 * the attempt on the first error, while the widget was already retrying, so a moment's poor connection on a phone told the donor
 * the check failed. Now a press waits this long for a retry's token (retries every RETRY_INTERVAL_MS), and only a configuration
 * error (Cloudflare's 110xxx family) or an unsupported browser ends it at once. Suspected, not proven, as the iPhone's cause: the
 * error code was not kept then; it is now ({@see lastError}) and reported to the site's event log.
 */
const RETRY_GRACE_MS = 7000;
const RETRY_INTERVAL_MS = 2000;

let scriptFailed = false;
// One widget per form's container: with two forms on a page, a module-wide id removed the
// other form's widget and left its Give waiting for ever (round 8, LOW-4).
const widgetIds = new WeakMap();
/*
 * F2 (rc3): what each container's widget holds: its id, the token not yet sent, whether the widget is in use, and who waits
 * for its next token. The widget is drawn once and kept; a token is sent once and then replaced in the same widget
 * (`turnstile.reset`), never by drawing a new widget per press of Continue to payment. Round 3's iPhone video: each press
 * drew a new widget, so the donor saw "Verifying…", "Verify you are human" and "Success" again every time.
 */
const widgets = new WeakMap();

/**
 * Loads the Turnstile script once, resolving with `window.turnstile`, or `null` if it never
 * loads within the timeout — the same fail-open-to-a-stated-fallback shape `gateway-loader.js`
 * uses, except here a missing widget means the donor cannot get a token at all, which
 * `Rest\PublicGate::verify_human()` already turns into the correct, fail-closed 403.
 *
 * @param {number} [timeoutMs]
 * @return {Promise<any|null>}
 */
function loadScript( timeoutMs = 8000 ) {
	if ( scriptPromise ) {
		return scriptPromise;
	}

	// An earlier load that timed out may still have finished: use it, not a second copy (round 10, LOW-4).
	if ( window.turnstile ) {
		return Promise.resolve( window.turnstile );
	}

	scriptPromise = new Promise( ( resolve ) => {
		let settled = false;

		const timer = window.setTimeout( () => {
			if ( ! settled ) {
				settled = true;
				resolve( null );
			}
		}, timeoutMs );

		const script = document.createElement( 'script' );

		script.src = SCRIPT_SRC;
		script.async = true;

		script.addEventListener( 'load', () => {
			window.clearTimeout( timer );

			if ( ! settled ) {
				settled = true;
				resolve( window.turnstile || null );
			}
		} );

		script.addEventListener( 'error', () => {
			window.clearTimeout( timer );

			if ( ! settled ) {
				settled = true;
				resolve( null );
			}
		} );

		document.head.appendChild( script );
	} );

	// A failed load is not kept: cached, every later attempt on a slow connection failed until the
	// page was reloaded (release gate 0.5.3, adversarial review round 9, LOW-3; the gateway
	// loader was fixed for the same in round 5).
	const loading = scriptPromise;

	loading.then( ( turnstile ) => {
		if ( null === turnstile && scriptPromise === loading ) {
			scriptPromise = null;
		}
	} );

	return loading;
}

/**
 * Settles everyone waiting on a widget's next token.
 *
 * @param {{waiters: Array<function(string|null): void>}} state
 * @param {string|null} token
 * @return {void}
 */
function settle( state, token ) {
	const waiting = state.waiters;

	state.waiters = [];
	waiting.forEach( ( resolve ) => resolve( token ) );
}

/**
 * Draws a widget into a container — `cData` is the checkout's own `sessionId`, which is what binds
 * the token to this session (`Security\Turnstile::verify()` compares them with `hash_equals`).
 *
 * A container that held an earlier widget which failed has it removed first, as Turnstile's API
 * asks, rather than relying on how a second render into an occupied container behaves (release
 * gate 0.5.3, adversarial review round 7).
 *
 * @param {any} turnstile
 * @param {HTMLElement} container
 * @param {string} siteKey
 * @param {string} sessionId
 * @param {{interactive?: function(boolean): void}} hooks
 * @return {Object} The widget's state.
 */
function draw( turnstile, container, siteKey, sessionId, hooks ) {
	if ( widgetIds.has( container ) && 'function' === typeof turnstile.remove ) {
		try {
			turnstile.remove( widgetIds.get( container ) );
		} catch ( error ) {
			// Already gone: nothing to remove.
		}
	}

	const state = { id: null, token: null, handedOut: false, broken: false, running: false, waiters: [], siteKey, sessionId, hooks, lastError: '', grace: null };

	// The latest press's hooks: a widget kept across presses (F2) tells the screen of the press now waiting.
	const interactive = ( on ) => {
		if ( 'function' === typeof state.hooks.interactive ) {
			state.hooks.interactive( on );
		}
	};

	/*
	 * What the security check is doing, for the screen: "checking" while it works, "passed" once it has given a token, "challenge"
	 * while Cloudflare asks the donor something (the only time anything of it is shown, 0.9.41), "failed" when it gave up.
	 */
	const report = ( what ) => {
		if ( 'function' === typeof state.hooks.status ) {
			state.hooks.status( what );
		}
	};

	widgets.set( container, state );

	// The attempt ends: whoever waits is told there is no token, and the next press draws the widget again.
	const fail = ( why ) => {
		window.clearTimeout( state.grace );
		state.grace = null;
		state.lastError = why;
		state.token = null;
		state.running = false;
		state.broken = true;
		report( 'failed' );
		settle( state, null );
	};

	// Working: a token, a challenge or a failure follows through the callbacks below (before render, which can call back at once).
	report( 'checking' );

	state.id = turnstile.render( container, {
		sitekey: siteKey,
		// `cData`, camel-cased: Turnstile's explicit-render option. Spelt `cdata` it is silently
		// ignored, Cloudflare echoes nothing back, and every real-key attempt is refused as not
		// bound to the session (staging 0.5.8). siteverify returns it lower-case as `cdata`.
		cData: sessionId,
		/*
		 * 0.9.41: invisible unless Cloudflare needs the donor. `interaction-only`: "The widget becomes visible only when visitor
		 * interaction is required"; `execution: 'execute'`: "The challenge runs after calling the `turnstile.execute()` function
		 * separately", which {@see getToken} does when the donor presses Continue to payment, never while they type
		 * (https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/widget-configurations/).
		 */
		appearance: 'interaction-only',
		execution: 'execute',
		// After rc5: Cloudflare's own retry, sooner than its default eight seconds, within RETRY_GRACE_MS.
		'retry-interval': RETRY_INTERVAL_MS,
		callback: ( token ) => {
			window.clearTimeout( state.grace );
			state.grace = null;
			state.lastError = '';
			state.running = false;
			report( 'passed' );

			// Handed straight to whoever waits for it (and so spent), or kept for the next press.
			if ( state.waiters.length > 0 ) {
				state.handedOut = true;
				settle( state, token );

				return;
			}

			state.token = token;
		},
		/*
		 * After rc5: Cloudflare is already retrying. A configuration error (110xxx) cannot pass on a retry and ends the attempt now;
		 * anything else waits up to RETRY_GRACE_MS for a retry's token. Returning true tells Turnstile the error was handled, so it
		 * logs nothing more to the console.
		 */
		'error-callback': ( code ) => {
			const why = String( code || 'error' );

			state.lastError = why;

			if ( /^110/.test( why ) || 0 === state.waiters.length ) {
				fail( why );

				return true;
			}

			if ( null === state.grace ) {
				state.grace = window.setTimeout( () => fail( state.lastError ), RETRY_GRACE_MS );
			}

			return true;
		},
		'unsupported-callback': () => fail( 'unsupported' ),
		/*
		 * A token left unsent for its five minutes expires, and the widget fetches the next one itself (Turnstile's
		 * `refresh-expired`, `auto` by default), which arrives through `callback`. Nothing is waiting for it here, so nobody
		 * is told anything (https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/widget-configurations/).
		 */
		'expired-callback': () => {
			state.token = null;
		},
		// A challenge left unanswered ends the attempt (round 9, LOW-3); the next press draws the widget again.
		'timeout-callback': () => fail( 'timeout' ),
		// Turnstile's own signals that the widget is asking the donor to act, and has stopped (0.9.38).
		'before-interactive-callback': () => {
			report( 'challenge' );
			interactive( true );
		},
		'after-interactive-callback': () => {
			report( 'checking' );
			interactive( false );
		},
	} );
	widgetIds.set( container, state.id );

	return state;
}

/**
 * Resolves with a token not yet sent: the one the widget already holds, or its next one.
 *
 * The widget is drawn on the first press of Continue to payment and kept for the rest of the visit (F2, rc3). A press
 * after a refusal reuses the token if it was never sent, or waits for the one {@see spendToken} asked for.
 *
 * @param {HTMLElement} container
 * @param {string} siteKey
 * @param {string} sessionId
 * @param {{interactive?: function(boolean): void}} [hooks] Told when the widget starts and stops asking the donor to act, so the
 *                                                         checkout's own time limit waits for a person (0.9.38).
 * @return {Promise<string|null>} Null when the script never loaded or no site key is configured.
 */
export async function getToken( container, siteKey, sessionId, hooks = {} ) {
	if ( '' === siteKey ) {
		return null;
	}

	const turnstile = await loadScript();

	scriptFailed = ! turnstile;

	if ( ! turnstile ) {
		return null;
	}

	/*
	 * A token handed out is spent, whether or not its request was ever answered. rc3's checkout smoke run: an attempt ended by
	 * the time limit never reached spendToken(), the retry sent the same token, and when the stalled request reached the server
	 * after it, that token was refused as used and its payment never completed. So a press after one whose token was not
	 * replaced asks the widget for the next token first.
	 */
	const previous = widgets.get( container );

	if ( previous && previous.handedOut && ! previous.token ) {
		spendToken( container );
	}

	let state = widgets.get( container );

	if ( ! state || state.broken || state.siteKey !== siteKey || state.sessionId !== sessionId || ! container.isConnected ) {
		state = draw( turnstile, container, siteKey, sessionId, hooks );
	} else {
		state.hooks = hooks;
	}

	if ( state.token ) {
		const token = state.token;

		state.token = null;
		state.handedOut = true;

		return token;
	}

	const waiting = new Promise( ( resolve ) => {
		state.waiters.push( resolve );
	} );

	// 0.9.41: the check runs now, on the press (`execution: 'execute'`). A widget already working is left to finish.
	if ( ! state.running && 'function' === typeof turnstile.execute && null !== state.id ) {
		state.running = true;

		try {
			turnstile.execute( state.id );
		} catch ( error ) {
			// Already running: its token arrives through the callback.
		}
	}

	return waiting;
}

/**
 * After rc5: why the last attempt ended without a token, for the site's event log: Cloudflare's error code, `timeout`,
 * `unsupported`, or `script` when Cloudflare's script never loaded. Empty when nothing failed.
 *
 * @param {HTMLElement} container
 * @return {string}
 */
export function lastError( container ) {
	const state = widgets.get( container );

	return scriptFailed ? 'script' : String( ( state && state.lastError ) || '' );
}

/**
 * Marks the widget's token as sent and asks the same widget for the next one, so the next press has a fresh token waiting.
 *
 * Called once the server has answered a request that carried the token, whatever it said: Cloudflare accepts a token once
 * (https://developers.cloudflare.com/turnstile/get-started/server-side-validation/), and so does `Security\Turnstile`.
 * `turnstile.reset` keeps the widget where it is; a stand-in without it has the widget drawn again on the next press.
 *
 * @param {HTMLElement} container
 * @return {void}
 */
export function spendToken( container ) {
	const state = widgets.get( container );

	// Already asked for the next one, or nothing was handed out: nothing to replace.
	if ( ! state || ! state.handedOut ) {
		return;
	}

	state.token = null;
	state.handedOut = false;
	// With `execution: 'execute'` the reset widget waits for the next press's `execute()` (getToken).
	state.running = false;

	const turnstile = window.turnstile;

	if ( turnstile && 'function' === typeof turnstile.reset && null !== state.id ) {
		try {
			turnstile.reset( state.id );

			return;
		} catch ( error ) {
			// A widget that cannot be reset is drawn again on the next press.
		}
	}

	state.broken = true;
}
